import React, { useEffect, useState } from 'react';
import { useAuthClient } from '@/lib/authClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle } from 'lucide-react';

export type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
  acceptMarketing: boolean;
};

type FormErrors = Partial<Record<keyof SignupFormValues, string>> & {
  acceptTerms?: string;
};

type SignupFormProps =
  | {
      signUpType: 'create';
      onSuccess: (values: SignupFormValues) => void;
      orgCreateToken: string;
    }
  | {
      signUpType: 'join';
      onSuccess: (values: SignupFormValues) => void;
      signupToken: string;
      presetEmail: string;
    };

export function SignupForm(props: SignupFormProps) {
  const { onSuccess } = props;
  const { signUp } = useAuthClient();
  const isJoin = props.signUpType === 'join';
  const [formData, setFormData] = useState<SignupFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptMarketing: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');

  // When joining via invite, prefill email from token and lock the field
  useEffect(() => {
    if (isJoin && props.signUpType === 'join' && props.presetEmail) {
      setFormData((prev) => ({ ...prev, email: props.presetEmail }));
    }
  }, [isJoin, props]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';
    else if (formData.firstName.trim().length < 2)
      newErrors.firstName = 'First name must be at least 2 characters';

    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (formData.lastName.trim().length < 2)
      newErrors.lastName = 'Last name must be at least 2 characters';

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      newErrors.email = 'Please enter a valid email address';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8)
      newErrors.password = 'Password must be at least 8 characters long';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password))
      newErrors.password =
        'Password must contain at least one uppercase letter, one lowercase letter, and one number';

    if (!formData.confirmPassword)
      newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';

    if (!formData.acceptTerms)
      newErrors.acceptTerms = 'You must accept the terms and conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof SignupFormValues,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Handle password matching validation in real-time
    if (field === 'password' || field === 'confirmPassword') {
      const newPassword =
        field === 'password' ? (value as string) : formData.password;
      const newConfirmPassword =
        field === 'confirmPassword'
          ? (value as string)
          : formData.confirmPassword;

      setErrors((prev) => {
        const { [field]: _omit, confirmPassword: _omitConfirm, ...rest } = prev;

        // Only validate password matching if confirmPassword has been entered
        if (newConfirmPassword) {
          if (newPassword !== newConfirmPassword) {
            return { ...rest, confirmPassword: 'Passwords do not match' };
          }
        }
        // Passwords match or confirmPassword is empty - no confirmPassword error
        return rest;
      });
    } else {
      // For non-password fields, just clear the error for that field
      if (errors[field]) {
        setErrors((prev) => {
          const { [field]: _omit, ...rest } = prev;
          return rest;
        });
      }
    }

    if (generalError) setGeneralError('');
  };

  const isFormValid = () => {
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      formData.password &&
      formData.confirmPassword &&
      formData.acceptTerms &&
      Object.keys(errors).length === 0
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setGeneralError('');
    try {
      const name = `${formData.firstName},${formData.lastName}`.trim();
      const result =
        props.signUpType === 'join'
          ? await signUp.emailAsInvitee({
              email: formData.email,
              password: formData.password,
              name,
              signupToken: props.signupToken,
            })
          : await signUp.emailAsOrgCreator({
              email: formData.email,
              password: formData.password,
              name,
              orgCreateToken: props.orgCreateToken,
            });

      const errorMessage = (result as any)?.error?.message;
      if (errorMessage) {
        setGeneralError(errorMessage);
        return;
      }
      onSuccess(formData);
    } catch (error) {
      console.error('Error signing up', error);
      setGeneralError(
        `An error occurred during signup. Please try again. ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {generalError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{generalError}</span>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              id="firstName"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`pl-10 ${errors.firstName ? 'border-destructive' : ''}`}
            />
          </div>
          {errors.firstName && (
            <p className="text-sm text-destructive">{errors.firstName}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              id="lastName"
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`pl-10 ${errors.lastName ? 'border-destructive' : ''}`}
            />
          </div>
          {errors.lastName && (
            <p className="text-sm text-destructive">{errors.lastName}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            id="email"
            type="text"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={isJoin}
            className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
          />
        </div>
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className={`pl-10 pr-10 ${errors.password ? 'border-destructive' : ''}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-slate-400" />
            ) : (
              <Eye className="h-4 w-4 text-slate-400" />
            )}
          </Button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password}</p>
        )}
      </div>

      <div className="space-y-2 mb-2">
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm password"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange('confirmPassword', e.target.value)
            }
            className={`pl-10 pr-10 ${
              errors.confirmPassword ? 'border-destructive' : ''
            }`}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4 text-slate-400" />
            ) : (
              <Eye className="h-4 w-4 text-slate-400" />
            )}
          </Button>
        </div>
        <p
          className={`text-sm text-destructive min-h-[20px] ${errors.confirmPassword ? 'visible' : 'invisible'}`}
        >
          {errors.confirmPassword || '\u00A0'}
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-start items-center space-x-2">
          <Checkbox
            id="acceptTerms"
            checked={formData.acceptTerms}
            onCheckedChange={(checked) =>
              handleInputChange('acceptTerms', checked === true)
            }
            className="mt-1"
          />
          <div className="grid gap-1.5 leading-none">
            <div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              <Label htmlFor="acceptTerms" className="cursor-pointer">
                I accept the
              </Label>{' '}
              <a
                href="https://onchainden.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-primary"
              >
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a
                href="https://onchainden.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-primary"
              >
                Privacy Policy
              </a>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">{errors.acceptTerms}</p>
            )}
          </div>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!isFormValid() || isLoading}
      >
        {isLoading ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Creating account...</span>
          </div>
        ) : (
          'Create Account'
        )}
      </Button>
    </form>
  );
}
